import type { IQrCodewords } from './IQrCodewords';
import { rsDivisor, rsRemainder } from './qrReedSolomon';
import { qrBlocks, qrDataCodewords, qrEccPerBlock, qrMaxVersion, qrRawModules } from './qrTables';

/** Довжина лічильника символів байтового режиму: 8 бітів у версіях 1–9, 16 — від 10. */
const countBits = (version: number): number => (version <= 9 ? 8 : 16);

/** Найменша версія (рівень M), що вміщує байти; null — дані задовгі. */
function versionFor(length: number): number | null {
  for (let v = 1; v <= qrMaxVersion; v++) if (4 + countBits(v) + 8 * length <= qrDataCodewords(v) * 8) return v;
  return null;
}

/** Слова даних: режим «байти» (0100), лічильник, байти, термінатор, вирівнювання до байта й заповнювачі 0xEC / 0x11. */
function dataCodewords(bytes: Uint8Array, version: number): number[] {
  const bits: number[] = [];
  const push = (value: number, length: number) => { for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1); };
  push(0b0100, 4);
  push(bytes.length, countBits(version));
  bytes.forEach((b) => push(b, 8));
  const capacity = qrDataCodewords(version) * 8;
  push(0, Math.min(4, capacity - bits.length));
  push(0, (8 - (bits.length % 8)) % 8);
  const words: number[] = [];
  for (let i = 0; i < bits.length; i += 8) words.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  for (let pad = 0xec; words.length < capacity / 8; pad ^= 0xec ^ 0x11) words.push(pad);
  return words;
}

/** Блоки даних із кодовими словами корекції, перемежовані: спершу i-те слово кожного блоку, далі слова корекції так само. */
function withEcc(data: number[], version: number): number[] {
  const blocks = qrBlocks(version);
  const eccLength = qrEccPerBlock(version);
  const raw = Math.floor(qrRawModules(version) / 8);
  const shortBlocks = blocks - (raw % blocks);
  const shortLength = Math.floor(raw / blocks);
  const divisor = rsDivisor(eccLength);
  const all: number[][] = [];
  for (let i = 0, k = 0; i < blocks; i++) {
    const chunk = data.slice(k, k + shortLength - eccLength + (i < shortBlocks ? 0 : 1));
    k += chunk.length;
    const ecc = rsRemainder(chunk, divisor);
    if (i < shortBlocks) chunk.push(0);
    all.push(chunk.concat(ecc));
  }
  const result: number[] = [];
  for (let i = 0; i < (all[0]?.length ?? 0); i++) {
    all.forEach((block, j) => { if (i !== shortLength - eccLength || j >= shortBlocks) result.push(block[i] ?? 0); });
  }
  return result;
}

/** Кодові слова QR-коду (рівень M, байтовий режим) для байтів; null — дані не вміщуються у версію 20. Чиста функція. */
export function qrCodewords(bytes: Uint8Array): IQrCodewords | null {
  const version = versionFor(bytes.length);
  return version === null ? null : { version, codewords: withEcc(dataCodewords(bytes, version), version) };
}
